import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Plus, Trash2, Users, PawPrint, GraduationCap, X, Loader2,
  ArrowLeft, Search, Save, CheckCircle2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVITY_META = {
  social_walks: { label: 'Social Walks & Meetups', icon: PawPrint, color: 'bg-emerald-500' },
  onsite_events: { label: 'Events & Activities', icon: Calendar, color: 'bg-amber-500' },
  dog_training: { label: 'Training & Learning', icon: GraduationCap, color: 'bg-purple-500' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const getAuth = () => {
  const t = localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const ActivityEventsAdmin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/activity-events`, { headers: getAuth() });
      setEvents(data);
    } catch (err) {
      toast.error('Could not load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleCreated = (ev) => {
    setEvents(prev => [ev, ...prev]);
    setShowCreate(false);
    setSelectedEventId(ev.event_id);
    toast.success(`Created "${ev.name}"`);
  };

  const handleDelete = async (eventId, name) => {
    if (!window.confirm(`Delete event "${name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/activity-events/${eventId}`, { headers: getAuth() });
      setEvents(prev => prev.filter(e => e.event_id !== eventId));
      toast.success('Event deleted');
    } catch (err) {
      toast.error('Could not delete event');
    }
  };

  if (selectedEventId) {
    return (
      <ActivityEventDetail
        eventId={selectedEventId}
        onBack={() => { setSelectedEventId(null); fetchEvents(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="activity-events-admin">
      {/* Header */}
      <div className="bg-white rounded-xl p-5 shadow-sm border flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#222] flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#0FA49C]" /> Activity Events
          </h2>
          <p className="text-sm text-[#666] mt-0.5">
            Create events, filter customers by activity, and mark who attended.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white"
          data-testid="create-event-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New event
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 border text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0FA49C]" />
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border text-center" data-testid="events-empty">
          <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-bold text-[#222]">No events yet</p>
          <p className="text-sm text-[#666] mt-1">Create your first event to start tracking attendance.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="events-list">
          {events.map(ev => {
            const meta = ACTIVITY_META[ev.activity_type] || { label: ev.activity_type, icon: Calendar, color: 'bg-gray-500' };
            const Icon = meta.icon;
            return (
              <div
                key={ev.event_id}
                onClick={() => setSelectedEventId(ev.event_id)}
                className="group bg-white rounded-xl border-2 border-gray-200 hover:border-[#0FA49C] hover:shadow-md transition-all duration-200 p-4 cursor-pointer relative"
                data-testid={`event-card-${ev.event_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 ${meta.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#222] truncate">{ev.name}</p>
                    <p className="text-xs text-[#666]">{meta.label}</p>
                    <div className="mt-2 flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1 text-[#444]">
                        <Calendar className="w-3 h-3" />
                        {formatDate(ev.event_date)}
                      </span>
                      <span className="inline-flex items-center gap-1 font-bold text-[#0FA49C]">
                        <Users className="w-3 h-3" />
                        {ev.attendee_count || 0}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(ev.event_id, ev.name); }}
                    className="w-7 h-7 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete event"
                    data-testid={`delete-event-${ev.event_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {ev.notes && (
                  <p className="text-xs text-[#666] mt-2 bg-gray-50 rounded-lg p-2 line-clamp-2">
                    {ev.notes}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {showCreate && (
          <CreateEventDialog onClose={() => setShowCreate(false)} onCreated={handleCreated} />
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create event dialog
// ---------------------------------------------------------------------------
const CreateEventDialog = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [activityType, setActivityType] = useState('social_walks');
  const [eventDate, setEventDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !eventDate) {
      toast.error('Name and date are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/activity-events`,
        { name: name.trim(), activity_type: activityType, event_date: eventDate, notes: notes.trim() || null },
        { headers: getAuth() },
      );
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-event-dialog"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#222] text-lg">New activity event</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-bold text-[#444] uppercase tracking-wide">Event name</label>
            <Input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Beach Social Walk"
              className="h-10 mt-1" disabled={saving} autoFocus
              data-testid="new-event-name"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-[#444] uppercase tracking-wide">Activity type</label>
            <div className="grid grid-cols-1 gap-1.5 mt-1">
              {Object.entries(ACTIVITY_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const checked = activityType === key;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-2.5 p-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                      checked ? 'border-[#0FA49C] bg-[#0FA49C]/5' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`new-event-type-${key}`}
                  >
                    <input
                      type="radio" name="activity_type" value={key}
                      checked={checked} onChange={() => setActivityType(key)}
                      className="w-4 h-4 accent-[#0FA49C]"
                    />
                    <div className={`w-7 h-7 ${meta.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm font-semibold">{meta.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-[#444] uppercase tracking-wide">Date</label>
            <Input
              type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
              className="h-10 mt-1" disabled={saving}
              data-testid="new-event-date"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-[#444] uppercase tracking-wide">Notes <span className="font-normal text-gray-400">(optional)</span></label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} placeholder="Meet at south car park at 10am"
              className="w-full mt-1 p-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#0FA49C]"
              disabled={saving}
              data-testid="new-event-notes"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#0FA49C] hover:bg-[#0d8f88]" data-testid="new-event-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : 'Create event'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Event detail — pick attendees from the eligible pool
// ---------------------------------------------------------------------------
const ActivityEventDetail = ({ eventId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/activity-events/${eventId}`, { headers: getAuth() });
        setData(data);
        setSelectedIds(new Set(data.event.attendee_customer_ids || []));
      } catch (err) {
        toast.error('Could not load event');
        onBack();
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const toggle = (customerId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
    setHasChanges(true);
  };

  const toggleAll = (checked) => {
    if (!data) return;
    if (checked) {
      setSelectedIds(new Set(filtered.map(c => c.customer_id)));
    } else {
      setSelectedIds(new Set());
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/activity-events/${eventId}`,
        { attendee_customer_ids: Array.from(selectedIds) },
        { headers: getAuth() },
      );
      toast.success(`Saved — ${selectedIds.size} attendee${selectedIds.size === 1 ? '' : 's'}`);
      setHasChanges(false);
    } catch (err) {
      toast.error('Could not save attendance');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.eligible_customers;
    return data.eligible_customers.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q),
    );
  }, [data, search]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 border text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0FA49C]" />
      </div>
    );
  }
  if (!data) return null;

  const { event } = data;
  const meta = ACTIVITY_META[event.activity_type] || { label: event.activity_type, icon: Calendar, color: 'bg-gray-500' };
  const Icon = meta.icon;
  const allTicked = filtered.length > 0 && filtered.every(c => selectedIds.has(c.customer_id));

  return (
    <div className="space-y-4" data-testid="activity-event-detail">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0FA49C] hover:underline"
        data-testid="event-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to events
      </button>

      {/* Event header */}
      <div className="bg-gradient-to-br from-[#0FA49C] to-[#0d8f88] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-11 h-11 ${meta.color} rounded-xl flex items-center justify-center shadow-md`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider opacity-80 font-semibold">{meta.label}</p>
            <h2 className="text-2xl font-extrabold truncate">{event.name}</h2>
            <p className="text-sm text-white/85">{formatDate(event.event_date)} · {selectedIds.size} attending</p>
          </div>
        </div>
        {event.notes && <p className="text-sm text-white/85 mt-2 italic">"{event.notes}"</p>}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl p-3 shadow-sm border flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="pl-8 h-9" data-testid="event-attendee-search"
          />
        </div>
        <button
          type="button"
          onClick={() => toggleAll(!allTicked)}
          className="text-xs font-bold text-[#0FA49C] hover:underline px-2"
          data-testid="event-attendee-toggle-all"
        >
          {allTicked ? 'Clear all' : 'Select all shown'}
        </button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] h-9"
          data-testid="event-save-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save attendance</>}
        </Button>
      </div>

      {/* Attendee list */}
      {data.eligible_customers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border text-center">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-bold text-[#222]">No eligible customers yet</p>
          <p className="text-sm text-[#666] mt-1">
            Customers who opt in to <strong>{meta.label}</strong> on <code>/ffos</code> will appear here.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border text-center text-sm text-[#666]">
          No customers match "{search}"
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <ul className="divide-y" data-testid="event-attendee-list">
            {filtered.map(c => {
              const checked = selectedIds.has(c.customer_id);
              return (
                <li key={c.customer_id}>
                  <label
                    className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${checked ? 'bg-[#0FA49C]/5' : ''}`}
                    data-testid={`event-attendee-row-${c.customer_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.customer_id)}
                      className="w-4 h-4 accent-[#0FA49C]"
                      data-testid={`event-attendee-checkbox-${c.customer_id}`}
                    />
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#666]">
                        {(c.name || c.email || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#222] text-sm truncate">{c.name || c.email}</p>
                      <p className="text-xs text-[#666] truncate">{c.email}</p>
                    </div>
                    {checked && <CheckCircle2 className="w-4 h-4 text-[#0FA49C] flex-shrink-0" />}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
